import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, FileText } from "lucide-react";
import { API } from "../data/constants";
import { formatPostDate, readingTimeFromHtml } from "../pages/blog/blogHelpers";

/**
 * Homepage "Latest from the blog" 3-card section. Renders nothing if no
 * published posts exist yet, so the homepage never shows an empty block
 * during initial onboarding.
 */
export const LatestBlogPosts = ({ limit = 3 }) => {
  const [posts, setPosts] = useState(null); // null = loading, [] = none

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/blog/posts`, { params: { limit } })
      .then(({ data }) => {
        if (!cancelled) setPosts(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [limit]);

  if (posts === null) return null;
  if (posts.length === 0) return null;

  return (
    <section className="section-padding bg-gray-light" data-testid="home-latest-blog-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-sm font-display font-bold uppercase tracking-wider text-orange-primary mb-2">Latest from the blog</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark">
              Foot-traffic playbooks & <span className="text-gradient-orange">SmartReach™ deep-dives</span>
            </h2>
          </div>
          <Link to="/blog" className="text-orange-primary font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-latest-blog-all-link">
            All posts <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="bg-white rounded-2xl shadow-card overflow-hidden card-hover block"
              data-testid={`home-latest-blog-${post.slug}`}
            >
              {post.cover_image_url ? (
                <img src={post.cover_image_url} alt={post.title} className="w-full h-48 object-cover" />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-orange-light to-orange-primary/20 flex items-center justify-center">
                  <FileText className="text-orange-primary/40" size={48} />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs text-gray-medium mb-2">
                  {post.category && <span className="bg-orange-primary/10 text-orange-primary px-2 py-0.5 rounded-full font-medium">{post.category}</span>}
                  <span className="inline-flex items-center gap-1"><Calendar size={11} /> {formatPostDate(post.published_at)}</span>
                  <span>· {readingTimeFromHtml(post.content)} min</span>
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2 line-clamp-2">{post.title}</h3>
                {post.excerpt && <p className="text-gray-medium text-sm line-clamp-2">{post.excerpt}</p>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LatestBlogPosts;
